import { createFileRoute, Link } from "@tanstack/react-router";
import { displayPhone } from "@alm/domain";
import { useQuery } from "@tanstack/react-query";
import { VISITOR_TYPE_LABELS } from "@alm/validation";
import { PageHeader } from "@/components/app/shell/page-header";
import { FactStrip } from "@/components/app/patterns/fact-strip";
import { RelativeTime } from "@/components/app/patterns/relative-time";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { useCan, useFeature } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { useZone } from "@/lib/facility/zone";
import { PersonDetailsCard } from "../-components/person-details-card";
import { PersonMemoryCard } from "../-components/person-memory-card";
import { PersonRecognitionCard } from "../-components/person-recognition-card";
import { PersonSecurityCard } from "../-components/person-security-card";
import { PersonSessionsCard } from "../-components/person-sessions-card";
import { PersonStatus } from "../-components/person-status";
import { PersonVisitsCard } from "../-components/person-visits-card";
import { VisitHistory } from "../-components/visit-history";
import { profileName } from "../-components/visitor-profile";

export const Route = createFileRoute("/_authenticated/_visitors/visitors/people/$personId")({
  beforeLoad: ({ context }) => guardRoute(context.me, "visitors"),
  component: VisitorPersonPage,
});

/** One visitor, one record: who they are, whether they may come in, what Grace knows, and every
 * visit. */
function VisitorPersonPage() {
  const { personId } = Route.useParams();
  const person = useQuery(orpc.visitors.people.get.queryOptions({ input: { personId } }));
  const facilityId = person.data?.facilityId ?? null;
  const canManage = useCan("visitor:manage", facilityId);
  const canSecure = useCan("visitor:security", facilityId);
  const graceOn = useFeature("grace", facilityId);
  const facesOn = useFeature("face-recognition", facilityId);
  const zone = useZone(facilityId);

  const back = (
    <Button asChild variant="ghost" size="sm" className="self-start">
      <Link to="/visitors/people">
        <ArrowLeft data-icon="inline-start" aria-hidden="true" />
        All visitors
      </Link>
    </Button>
  );

  if (person.isError) {
    return (
      <div className="flex flex-col gap-5">
        {back}
        <Card className="gap-0 py-0">
          <ReadFailed
            className="p-4"
            message="Could not load this visitor."
            error={person.error}
            onRetry={() => void person.refetch()}
            retrying={person.isFetching}
          />
        </Card>
      </div>
    );
  }

  const row = person.data;
  if (!row) {
    return (
      <div className="flex flex-col gap-5" aria-busy="true">
        {back}
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const name = profileName(row);
  return (
    <div className="flex flex-col gap-5">
      {back}
      <PageHeader
        title={
          row.preferredName ? (
            <>
              {name}
              <span className="text-muted-foreground ms-2 text-lg font-normal">
                goes by {row.preferredName}
              </span>
            </>
          ) : (
            name
          )
        }
        meta={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <PersonStatus
              blocked={row.block !== null}
              enrolled={row.recognition.enrolled}
              showFace={facesOn}
            />
            <span>
              {[VISITOR_TYPE_LABELS[row.visitorType], row.company].filter(Boolean).join(", ")}
            </span>
          </span>
        }
      />
      <FactStrip
        label="About this visitor"
        facts={[
          { label: "Visits", value: <span className="tabular-nums">{row.stats.totalVisits}</span> },
          {
            label: "Last visit",
            value: <RelativeTime value={row.stats.lastVisitAt} timeZone={zone} />,
          },
          {
            label: "First visit",
            value: <RelativeTime value={row.stats.firstVisitAt} timeZone={zone} />,
          },
          {
            label: "Last 90 days",
            value: <span className="tabular-nums">{row.stats.visitsLast90Days}</span>,
          },
          {
            label: "Phone",
            value: row.phone ? (
              <span className="tabular-nums">{displayPhone(row.phone)}</span>
            ) : (
              "-"
            ),
          },
          { label: "Email", value: row.email ?? "-" },
          {
            label: "Company",
            value: row.company ?? "-",
            sub: row.companyKind ? "From the directory" : row.company ? "As typed" : undefined,
          },
        ]}
      />
      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="flex min-w-0 flex-col gap-5">
          <PersonVisitsCard stats={row.stats} />
          <VisitHistory
            source={{ kind: "list", filter: { facilityId: "all", personId } }}
            emptyTitle="No visits recorded"
          />
          {graceOn ? (
            <PersonMemoryCard personId={row.id} facilityId={row.facilityId} canManage={canManage} />
          ) : null}
          {graceOn ? <PersonSessionsCard person={row} canSecure={canSecure} /> : null}
        </div>
        <div className="flex min-w-0 flex-col gap-5">
          <PersonDetailsCard key={String(row.updatedAt)} person={row} canManage={canManage} />
          <PersonSecurityCard person={row} canSecure={canSecure} />
          {facesOn && canSecure ? (
            <PersonRecognitionCard
              personId={row.id}
              personName={name}
              facilityId={row.facilityId}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
